import React, {useEffect} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import {SelectedFilters, TabSwitcher} from 'components';
import {systemActions} from 'store';
import {ClaimFilters} from './claimFilters';
import {authEnums, billingStatusList, claimTabEnums, claimTabTypes, submittedViaList} from './constants';
import {completedClaimStatusList} from '../completed/constants';
import {PendingClaims} from './pendingClaims';
import {BillableAppt} from './billableAppt';
import {SubmittedClaim} from './submittedClaim';
import {CompletedClaims} from '../completed';
import {PermissionList, RolePermission, renderClientName, renderDateRange, saveLocalParams} from 'utils';
import {secondaryBillingList} from '../claimPayment/constants';
import {APPEAL} from '../../../fragments/billing/claims/claimDetails/constants';

/**
 * Drop keys from the persisted `claimFilters` snapshot without rebuilding it.
 *
 * The snapshot is written from several places in ClaimFilters, each persisting a slightly
 * different subset, and the Billable tab restores it wholesale on mount. Rebuilding it here
 * would silently add or drop keys those writers own; deleting only what the chip covers
 * leaves the rest untouched. Without this a removed filter comes straight back on the next
 * tab switch.
 */
const dropFromClaimSnapshot = (keys) => {
    const raw = sessionStorage.getItem('claimFilters');
    if (!raw) return;
    let snapshot;
    try {
        snapshot = JSON.parse(raw) || {};
    } catch {
        return; // Corrupt snapshot — the restore effect ignores it too.
    }
    keys.forEach((k) => delete snapshot[k]);
    saveLocalParams('claimFilters', snapshot);
};

export const Claims = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;
    const checkTableType =
        info?.tabType === claimTabEnums?.BILLABLE
            ? claimTabEnums?.BILLABLE
            : info?.tabType === claimTabEnums?.PENDING
                ? claimTabEnums?.PENDING
                : info?.tabType === claimTabEnums?.SUBMITTED
                    ? claimTabEnums?.SUBMITTED
                    : info?.tabType === claimTabEnums?.COMPLETED
                        ? claimTabEnums?.COMPLETED
                        : claimTabEnums?.BILLABLE;
    const checkTabType = info?.tabType === claimTabEnums?.BILLABLE || !info?.tabType;

    // Mirrors the gating behind ClaimFilters' `activeFiltersCount` badge — a filter that is
    // hidden on this tab (or in Batch View) is not applied, so it must not show a chip either.
    const isBillable = checkTabType;
    const isSubmitted = info?.tabType === claimTabEnums?.SUBMITTED;
    const isCompleted = info?.tabType === claimTabEnums?.COMPLETED;
    const batchView = !!info?.batchView;

    /**
     * Every filter the Claims tabs can carry, declared once.
     *
     * `show` mirrors the gating behind ClaimFilters' `activeFiltersCount` badge — a filter that is
     * hidden on this tab (or in Batch View) is not applied, so it must not show a chip either — but
     * "Clear Filters" still clears every key regardless of tab, or a filter hidden on the current
     * tab survives invisibly into the one that applies it.
     */
    const filtersList = [
        {name: 'Payer', key: 'payer', companions: ['funderName'], show: !batchView, render: (v, state) => state?.funderName || v},
        {
            name: 'Client',
            key: 'client',
            companions: ['clientFull'],
            show: !batchView,
            render: (v, state) => renderClientName(state?.clientFull),
        },
        {
            name: 'Auth#',
            key: 'withoutAuthId',
            show: !batchView && isBillable,
            render: (v) => (v === authEnums.WITH_AUTH ? 'With Auth #' : 'Without Auth #'),
        },
        // One chip per errored reason, each removable on its own (same as ScheduleInformation).
        {name: 'Errored', key: 'errors', show: !batchView && isBillable, render: (e) => e?.name ?? e},
        {
            name: 'Submission Status',
            key: 'clearinghouseStatus',
            show: !batchView && isSubmitted,
            options: billingStatusList,
        },
        {
            name: 'Sec. Billing',
            key: 'secondaryBillingState',
            show: !batchView && isSubmitted,
            options: secondaryBillingList,
        },
        // DOS and Submitted Date stay visible in Batch View — that view only collapses the rest.
        {name: 'DOS', keys: ['from', 'to'], render: ([from, to]) => renderDateRange(from, to)},
        {
            name: 'Submitted',
            keys: ['submittedFrom', 'submittedTo'],
            show: isSubmitted,
            render: ([from, to]) => renderDateRange(from, to),
        },
        // `status` carries two unrelated meanings, one per tab — 'w/o appeals' on Submitted, the
        // Completed tab's Status column elsewhere. The tabs are mutually exclusive so only one of
        // these can ever render, and both clear the same key. Absent on Completed means that tab's
        // own COMPLETED default, which is not a user filter and gets no chip.
        {label: 'w/o appeals', key: 'status', show: !batchView && isSubmitted && info?.status === APPEAL},
        {name: 'Status', key: 'status', show: !batchView && isCompleted, options: completedClaimStatusList},
        // Table column-header filter — the Submitted tab's "Source". Batch View strips every column
        // filter (`tableInfoToFullTable` nulls `filterList`), so no chip there.
        {name: 'Source', key: 'submittedVia', show: !batchView, options: submittedViaList},
    ];

    /**
     * Keep the `claimFilters` sessionStorage snapshot in step with the state write.
     *
     * The Billable tab restores that snapshot wholesale on mount, so a filter removed here has to
     * leave it too or it comes straight back on the next tab switch. Deleting only the keys the
     * change touched — rather than rebuilding the snapshot — leaves the rest alone, since several
     * writers in ClaimFilters each own a different subset of it.
     */
    const handleFilterChange = (params, {removedKeys}) => dropFromClaimSnapshot(removedKeys);

    useEffect(() => {
        dispatch(systemActions.canUseIntegration());
    }, []);

    useEffect(() => {
        const claimFilters = sessionStorage.getItem('claimFilters');
        if (claimFilters && checkTabType) {
            const mergedState = {
                ...(claimFilters ? JSON.parse(claimFilters) : {}),
            };
            navigate(location.pathname, {state: mergedState});
        }
    }, [checkTabType]);

    return (
        <div>
            {/*<div className={checkTableType === claimTabEnums?.SUBMITTED ? 'space-between' : 'claim-filters-wrapper'}>*/}
            <div className='space-between'>
                <TabSwitcher
                    list={claimTabTypes}
                    defaultType={claimTabEnums?.BILLABLE}
                    saveWidthAction={(type) => {
                        if (type === claimTabEnums?.SUBMITTED) {
                            return {
                                sortType: 'subDate',
                                orderType: 'DESC',
                            }
                        }
                    }}
                />
                <div className="claim-filter-box">
                    <ClaimFilters/>
                </div>
            </div>

            <div style={{marginTop: 12}}>
                <SelectedFilters
                    filtersList={filtersList}
                    resetParams={{skip: 0, limit: 50, page: 1}}
                    onChange={handleFilterChange}
                    doNotCrop
                />
            </div>

            {checkTableType === claimTabEnums?.BILLABLE && <BillableAppt/>}

            {checkTableType === claimTabEnums?.PENDING && <PendingClaims/>}

            {checkTableType === claimTabEnums?.SUBMITTED && <SubmittedClaim/>}

            {checkTableType === claimTabEnums?.COMPLETED && RolePermission([PermissionList.CLAIMS_READ?.code]) &&
                <CompletedClaims/>}
            {/*{checkTableType === claimTabEnums?.COMPLETED && <CompletedBillClaims />}*/}
        </div>
    );
};
